import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { filter } from 'lodash-es'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { alpha, Tab, useTheme } from '@mui/material'
import { EmptyState } from '@ui2/feedback'
import Box from '@ui/structure/Box'
import { useEditorViewStore, useResultsStore } from '@editorStores'
import { useResultsSelection } from '@editorHooks'
import { useSelectResults2Element } from '../../hooks/useSelectResults2Element'
import AreaLoadTable from '../LoadTables/AreaLoadTable'
import DatagridLoadWrapper from '../LoadTables/DatagridLoadWrapper'
import LineLoadTable from '../LoadTables/LineLoadTable'
import PointLoadTable from '../LoadTables/PointLoadTable'

type LoadTab = 'point-load' | 'line-load' | 'area-load'

interface Props {
  loads: Array<DomainLoad | AreaLoad> | null
  defaultLoadTab?: LoadTab
  elementId?: string
}

const Results2LoadResults = ({
  loads,
  defaultLoadTab = 'point-load',
  elementId,
}: Props): ReactElement => {
  const theme = useTheme()
  const selectSourceElement = useSelectResults2Element(elementId)
  const [localHiddenLoads, setLocalHiddenLoads] = useState<string[]>([])
  const setHiddenLoads = useResultsStore(state => state.setHiddenLoads)
  const clearHiddenLoads = useResultsStore(state => state.clearHiddenLoads)

  const storeLoadTab = useEditorViewStore(state => state.results.loadTab) as LoadTab | null
  const setResultsLoadTab = useEditorViewStore(state => state.setResultsLoadTab)
  const tracedLoads = useEditorViewStore(state => state.results.tracedLoads)
  const setTracedLoads = useEditorViewStore(state => state.setResultsTracedLoads)

  const loadTab = storeLoadTab ?? defaultLoadTab

  const { activeMode, selectedLoad, selectResultsLoad: selectLoad } = useResultsSelection()
  const isAnchorCalculationMode = activeMode === 'tensile-connectors-table'

  const { lineLoads, pointLoads, areaLoads } = useMemo(
    () => ({
      lineLoads: loads && (filter(loads, ['load_type', 'line-load']) as LineLoadWithDomain[]),
      pointLoads: loads && (filter(loads, ['load_type', 'point-load']) as PointLoadWithDomain[]),
      areaLoads: loads && (filter(loads, ['load_type', 'area-load']) as AreaLoad[]),
    }),
    [loads],
  )

  const [selectedCategories] = useState<LoadCategory[]>(
    isAnchorCalculationMode ? ['Dead'] : ['Dead', 'Live', 'Snow', 'SnowPlus', 'Wind', 'EarthQuake'],
  )

  const allHiddenLoads = useMemo(
    () => [
      ...localHiddenLoads,
      ...filter(
        [...(lineLoads || []), ...(pointLoads || []), ...(areaLoads || [])],
        load => !selectedCategories.includes(load.load_case.category),
      ).map(load => load.guid),
    ],
    [localHiddenLoads, lineLoads, pointLoads, areaLoads, selectedCategories],
  )

  useEffect(() => {
    setResultsLoadTab(defaultLoadTab)
    setLocalHiddenLoads([])
  }, [defaultLoadTab, setResultsLoadTab])

  useEffect(() => {
    if (loadTab === 'area-load') {
      setHiddenLoads(allHiddenLoads)
    }
    return clearHiddenLoads
  }, [allHiddenLoads, clearHiddenLoads, setHiddenLoads, loadTab])

  const tableStyleSx = {
    '& .MuiDataGrid-root': {
      border: 0,
      fontFamily: 'Roboto',
      backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader .MuiTypography-root': {
      fontSize: 12,
      color: theme.palette.text.secondary,

      fontWeight: 400,
      letterSpacing: 0,
    },
    '& .MuiDataGrid-cell, & .MuiDataGrid-cell .MuiTypography-root': {
      fontSize: 12,
      color: theme.palette.text.primary,
      fontFamily: 'Roboto',
    },
    '& .MuiDataGrid-root .MuiDataGrid-columnHeader': {
      px: 0.75,
    },
    '& .MuiDataGrid-root .MuiDataGrid-cell': {
      px: 0.75,
      borderColor: theme.palette.divider,
    },
    '& .MuiDataGrid-columnSeparator': {
      opacity: 0,
    },
    '& .MuiDataGrid-toolbarContainer': {
      minHeight: 28,
      p: 0,
      pb: 0.75,
    },
    '& .MuiDataGrid-toolbarContainer .MuiStack-root': {
      alignItems: 'center',
      gap: 1,
    },
    '& .MuiDataGrid-toolbarContainer .MuiStack-root > :not(style) ~ :not(style)': {
      ml: 1,
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
      minHeight: 24,
      px: 0.75,
      py: 0.25,
      fontSize: 11,
      lineHeight: 1.2,
    },
    '& .MuiDataGrid-toolbarContainer .MuiInputBase-root': {
      fontSize: 11,
    },
    '& .MuiDataGrid-toolbarContainer .MuiInputBase-input': {
      py: 0.25,
    },
    '& .MuiDataGrid-toolbarContainer .MuiFormControlLabel-root': {
      m: 0,
      gap: 0.5,
    },
    '& .MuiDataGrid-toolbarContainer .MuiFormControlLabel-label': {
      fontSize: 11,
      color: theme.palette.text.secondary,
    },
    '& .MuiDataGrid-headerFilterRow .MuiInputBase-root': {
      fontSize: 11,
    },
    '& .MuiDataGrid-headerFilterRow .MuiInputBase-input': {
      py: 0.5,
      px: 0.5,
    },
    // Reduce visual noise in filter bar without shrinking row height.
    '& .MuiDataGrid-headerFilterRow .MuiInputBase-input::placeholder': {
      opacity: 0,
    },
    '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
      backgroundColor: alpha(theme.palette.warning.main, 0.22),
    },
  } as const

  return (
    <Box>
      <TabContext value={loadTab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: 12,
              paddingX: ({ spacing }) => spacing(1.5),
            },
          }}
        >
          <TabList
            scrollButtons="auto"
            onChange={(_, value) => {
              setResultsLoadTab(value)
              setLocalHiddenLoads([])
            }}
          >
            <Tab value="point-load" label="Punktlasten" />
            <Tab value="line-load" label="Linienlasten" />
            <Tab value="area-load" label="Flächenlasten" />
          </TabList>
        </Box>
        <TabPanel value="point-load" sx={{ px: 0, pt: 1.25, pb: 0, ...tableStyleSx }}>
          {pointLoads ? (
            <DatagridLoadWrapper
              component={({ pointLoads, isAnchorCalculationMode, hiddenLoads, setHiddenLoads }) => (
                <PointLoadTable
                  pointLoads={pointLoads}
                  deadLoadFilterByDefault={isAnchorCalculationMode}
                  hiddenLoads={hiddenLoads}
                  setHiddenLoads={setHiddenLoads}
                  selectedLoad={selectedLoad}
                  onSelectLoad={selectLoad}
                  onSelectSource={selectSourceElement}
                  tracedLoads={tracedLoads}
                  onTraceLoads={setTracedLoads}
                />
              )}
              loads={loads || []}
            />
          ) : (
            <EmptyState>Daten noch nicht verfügbar</EmptyState>
          )}
        </TabPanel>
        <TabPanel value="line-load" sx={{ px: 0, pt: 1.25, pb: 0, ...tableStyleSx }}>
          {lineLoads ? (
            <DatagridLoadWrapper
              component={({ lineLoads, isAnchorCalculationMode, hiddenLoads, setHiddenLoads }) => (
                <LineLoadTable
                  lineLoads={lineLoads}
                  deadLoadFilterByDefault={isAnchorCalculationMode}
                  hiddenLoads={hiddenLoads}
                  setHiddenLoads={setHiddenLoads}
                  selectedLoad={selectedLoad}
                  onSelectLoad={selectLoad}
                  onSelectSource={selectSourceElement}
                  tracedLoads={tracedLoads}
                  onTraceLoads={setTracedLoads}
                />
              )}
              loads={loads || []}
            />
          ) : (
            <EmptyState>Daten noch nicht verfügbar</EmptyState>
          )}
        </TabPanel>
        <TabPanel value="area-load" sx={{ px: 0, pt: 1.25, pb: 0, ...tableStyleSx }}>
          {areaLoads ? (
            <DatagridLoadWrapper
              component={({ areaLoads, isAnchorCalculationMode, hiddenLoads, setHiddenLoads }) => (
                <AreaLoadTable
                  areaLoads={areaLoads}
                  deadLoadFilterByDefault={isAnchorCalculationMode}
                  hiddenLoads={hiddenLoads}
                  setHiddenLoads={setHiddenLoads}
                  selectedLoad={selectedLoad}
                  onSelectLoad={selectLoad}
                />
              )}
              loads={loads || []}
            />
          ) : (
            <EmptyState>Daten noch nicht verfügbar</EmptyState>
          )}
        </TabPanel>
      </TabContext>
    </Box>
  )
}

export default Results2LoadResults
